import _ from "lodash";

import ACLStore from "lib/aclStore";

const RNP_MAPPING_FEATURE_FLAG = "FeatureFlag:RolesAndPermissionSync";

// Check if RnP mapping feature is enabled. Same flag gates LR producer.
export const isRnpMappingFeatureEnabled = () =>
  ACLStore.can(RNP_MAPPING_FEATURE_FLAG);

// Tab keys for the RnP mapping screen.
export const RNP_TABS = {
  DEFAULTS: "DEFAULTS",
  PROGRAM: "PROGRAM",
  CLASS: "CLASS",
  LC: "LC",
};

// Iteration order for role-type-keyed state and serialized saves.
// The DEFAULTS tab is not a role type; it's just a tab.
export const ORDERED_ROLE_TYPES = [
  RNP_TABS.PROGRAM,
  RNP_TABS.CLASS,
  RNP_TABS.LC,
];

// Sort SIS-id arrays inside a role-type mapping object so option-order tweaks
// don't register as changes when diffing local state against saved state.
export const sortMappings = mappings =>
  _.mapValues(mappings, ids => _.sortBy(ids));

// Reduce subTaskFeed edges to a [{serviceId, name}] catalog used by the
// per-role-type mapping table dropdowns.
export const parseSisRolesCatalog = edges =>
  _.compact(
    _.map(edges, edge => {
      const parsedData = _.get(edge, "node.parsedData");
      if (!parsedData) return null;
      return { serviceId: parsedData.serviceId, name: parsedData.name };
    })
  );

// Map roleType -> entityType for V1 thirdPartyMappings + V2 save mutation.
export const ROLE_TYPE_TO_ENTITY_TYPE = {
  [RNP_TABS.PROGRAM]: "PROGRAM_ROLE",
  [RNP_TABS.CLASS]: "CLASS_ROLE",
  [RNP_TABS.LC]: "LC_ROLE",
};

// Map roleType -> parsed_data sub_task_type emitted by LR.
export const ROLE_TYPE_TO_SUBTASK_TYPE = {
  [RNP_TABS.PROGRAM]: "PROGRAM_ROLE",
  [RNP_TABS.CLASS]: "CLASS_ROLE",
  [RNP_TABS.LC]: "LC_ROLE",
};

// Map roleType -> RnPRole.globalLevel value to filter Toddle roles by.
// `globalLevel` is the server-side field name on RnPRole; keep that spelling.
export const ROLE_TYPE_TO_GLOBAL_LEVEL = {
  [RNP_TABS.PROGRAM]: "PROGRAMME",
  [RNP_TABS.CLASS]: "COURSE",
  [RNP_TABS.LC]: "LEARNING_COURSE",
};

// Default-role globalConstants name keys (per-curriculum scoped).
export const DEFAULT_ROLE_CONSTANT_NAMES = {
  PROGRAM: "RNP_DEFAULT_PROGRAM_ROLE",
  CLASS: "RNP_DEFAULT_CLASS_ROLE",
  LC: "RNP_DEFAULT_LC_ROLE",
};

export const ALL_DEFAULT_ROLE_NAMES = _.values(DEFAULT_ROLE_CONSTANT_NAMES);

export const getTabList = ({ t }) => [
  {
    value: RNP_TABS.DEFAULTS,
    label: t("integration:defaults"),
  },
  {
    value: RNP_TABS.PROGRAM,
    label: t("integration:program_role"),
  },
  {
    value: RNP_TABS.CLASS,
    label: t("integration:class_role"),
  },
  {
    value: RNP_TABS.LC,
    label: t("integration:lc_role"),
  },
];

// Columns: left = Toddle role, right = multi-select of SIS roles.
export const getColumnList = ({ serviceName, roleType, t }) => {
  const service = serviceName || "";

  let rightLabel;
  if (roleType === RNP_TABS.PROGRAM) {
    rightLabel = t("integration:sis_job_titles_with_service", { service });
  } else {
    rightLabel = t("integration:sis_class_roles_with_service", { service });
  }

  return [
    {
      label: t("integration:toddle_role"),
      id: "toddleRole",
      type: "TEXTCOLUMN",
      isSticky: true,
      style: { width: "40%" },
    },
    {
      label: rightLabel,
      id: "sisRoleMultiSelect",
      type: "CUSTOM",
      isSticky: true,
      style: { width: "60%" },
    },
  ];
};

// One row per Toddle role for this role type. Mirror alert mapping shape:
// flat string for TEXTCOLUMN, nested object (with toddleRoleId) for CUSTOM.
export const generateFeedData = ({ toddleRoles, t }) => {
  return _.map(toddleRoles, role => ({
    id: role.id,
    toddleRoleId: role.id,
    toddleRole: getToddleRoleDisplayLabel({ role, t }),
    sisRoleMultiSelect: {
      toddleRoleId: role.id,
    },
  }));
};

// Filter all org RnP roles down to those matching this role type's globalLevel.
export const getToddleRolesForRoleType = ({ rnpRoles = [], roleType }) => {
  const targetGlobalLevel = ROLE_TYPE_TO_GLOBAL_LEVEL[roleType];
  return _.filter(
    rnpRoles,
    role => _.get(role, "globalLevel") === targetGlobalLevel
  );
};

// RnPRole.label is an i18n key in the acl namespace (e.g. role_programme_admin).
// Resolve to human display text. Falls back to the raw key, then description.
export const getToddleRoleDisplayLabel = ({ role, t }) => {
  const key = _.get(role, "label");
  if (key) return t(`acl:${key}`);
  return _.get(role, "description") || "";
};

// SelectDropdown options for the DefaultsPanel pickers.
export const getToddleRoleOptions = ({ rnpRoles = [], roleType, t }) =>
  _.map(getToddleRolesForRoleType({ rnpRoles, roleType }), role => ({
    value: role.id,
    label: getToddleRoleDisplayLabel({ role, t }),
  }));

// SelectDropdown options from the SIS catalog (parsed_data rows).
export const getSisRoleOptions = ({ sisRoles = [] }) =>
  _.map(sisRoles, role => ({
    value: role.serviceId,
    label: role.name || role.serviceId,
  }));

// Row label for the Defaults panel — one row per role type.
export const getDefaultsRowLabel = ({ roleType, t }) => {
  if (roleType === RNP_TABS.PROGRAM) {
    return t("integration:default_program_role");
  }
  if (roleType === RNP_TABS.CLASS) {
    return t("integration:default_class_role");
  }
  return t("integration:default_lc_role");
};

// Columns for the Defaults panel table.
export const getDefaultsColumnList = ({ t, serviceName }) => [
  {
    label: t("common:role"),
    id: "roleLabel",
    type: "TEXTCOLUMN",
    isSticky: true,
    style: { width: "40%" },
  },
  {
    label: t("integration:default_toddle_role_with_service", {
      service: serviceName || "",
    }),
    id: "defaultRoleDropdown",
    type: "CUSTOM",
    isSticky: true,
    style: { width: "60%" },
  },
];

// Hide SIS options already used by another Toddle role on the same tab
// (a given SIS key maps to at most one Toddle role under ONE_TO_MANY).
export const getFilteredSisRoleOptions = ({
  sisRoles,
  mappingsState,
  toddleRoleId,
}) => {
  const allOptions = getSisRoleOptions({ sisRoles });
  const usedElsewhere = new Set();
  _.forEach(mappingsState, (sisIds, otherToddleId) => {
    if (otherToddleId === toddleRoleId) return;
    _.forEach(sisIds, id => usedElsewhere.add(id));
  });
  return _.filter(allOptions, opt => !usedElsewhere.has(opt.value));
};

// V1 thirdPartyMappings returns [{thirdPartyId, toddleId}]. Bucket by toddleId.
export const convertSavedMappingsToState = ({ mappings = [] }) => {
  const obj = {};
  _.forEach(mappings, ({ toddleId, thirdPartyId }) => {
    if (!toddleId || !thirdPartyId) return;
    if (!obj[toddleId]) obj[toddleId] = [];
    if (!obj[toddleId].includes(thirdPartyId)) obj[toddleId].push(thirdPartyId);
  });
  return obj;
};

// Flatten {toddleId: [sisIds]} back into [{toddleId, thirdPartyId}] for save.
// Empty selection emits {toddleId, thirdPartyId: ""} so the controller clears
// existing rows for that toddleId (mirrors alert mapping save shape).
export const flattenMappingsForSave = ({ mappingsState, toddleRoles }) => {
  return _.flatMap(toddleRoles, role => {
    const sisIds = mappingsState[role.id] || [];
    if (_.isEmpty(sisIds)) {
      return [{ toddleId: role.id, thirdPartyId: "" }];
    }
    return _.map(sisIds, sisId => ({ toddleId: role.id, thirdPartyId: sisId }));
  });
};
